import { useRef, useState, useCallback, useEffect } from 'react';
import { motion, useScroll, useTransform, useSpring, useMotionValue, useInView } from 'framer-motion';

/* ── Team Data ── */
const founder = {
  initials: 'FD',
  role: 'Founder & CEO',
  desc: 'The conviction behind the studio. Shaping ventures at the intersection of creativity, AI, and execution.',
  img: 'https://images.unsplash.com/photo-1646310101356-2b7fae79345a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxCbGFjayUyMG1hbiUyMGRyZWFkbG9ja3MlMjBzdWl0JTIwbG9va2luZyUyMGF3YXklMjBzaWRlJTIwcHJvZmlsZSUyMHNlcmlvdXMlMjBwb3dlcmZ1bCUyMGRhcmslMjBwb3J0cmFpdHxlbnwwfHx8fDE3Nzc4NTk0MDJ8MA&ixlib=rb-4.1.0&q=85',
};

const crew = [
  {
    initials: 'CD',
    role: 'Creative Director',
    desc: 'Brand language, visual direction, and the taste that defines the studio identity.',
    img: 'https://images.unsplash.com/photo-1764545973653-94c40d993495?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMGRhcmslMjBiYWNrZ3JvdW5kJTIwc3R1ZGlvfGVufDB8fHx8MTc3NTYxNzY3MXww&ixlib=rb-4.1.0&q=85',
  },
  {
    initials: 'AE',
    role: 'AI Engineer',
    desc: 'Systems architecture, agent design, and the technical intelligence behind every build.',
    img: 'https://images.unsplash.com/photo-1770894807442-108cc33c0a7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMGRhcmslMjBiYWNrZ3JvdW5kJTIwc3R1ZGlvfGVufDB8fHx8MTc3NTYxNzY3MXww&ixlib=rb-4.1.0&q=85',
  },
  {
    initials: 'PS',
    role: 'Product Strategist',
    desc: 'User research, roadmaps, and the execution discipline that turns plans into products.',
    img: 'https://images.unsplash.com/photo-1751818397262-040cddef4390?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHx3b21hbiUyMHByb2Zlc3Npb25hbCUyMHBvcnRyYWl0JTIwZGFyayUyMG1pbmltYWx8ZW58MHx8fHwxNzc1NjE3NjgzfDA&ixlib=rb-4.1.0&q=85',
  },
  {
    initials: 'DL',
    role: 'Design Lead',
    desc: 'Interface craft, motion systems, and the precision that makes every pixel intentional.',
    img: 'https://images.pexels.com/photos/33261959/pexels-photo-33261959.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  },
];

/* ── Animated Text Reveal ── */
function SplitText({ text, delay = 0, className = '' }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <span ref={ref} className={`split-text-wrap ${className}`} aria-label={text}>
      {text.split('').map((char, i) => (
        <motion.span
          key={i}
          className="split-char"
          initial={{ opacity: 0, y: 20, rotateX: -90 }}
          animate={isInView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
          transition={{
            duration: 0.5,
            delay: delay + i * 0.025,
            ease: [0.16, 1, 0.3, 1],
          }}
        >
          {char === ' ' ? '\u00A0' : char}
        </motion.span>
      ))}
    </span>
  );
}

/* ── Founder Hero Card ── */
function FounderCard() {
  const ref = useRef(null);
  const imgRef = useRef(null);
  const [hover, setHover] = useState(false);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springX = useSpring(mouseX, { stiffness: 100, damping: 25 });
  const springY = useSpring(mouseY, { stiffness: 100, damping: 25 });
  const [spotPos, setSpotPos] = useState({ x: 50, y: 50 });

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const imgY = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const textY = useTransform(scrollYProgress, [0, 1], [30, -30]);
  const rotate = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [2, 0, 0, -1]);

  const handleMove = useCallback((e) => {
    if (!imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    mouseX.set((e.clientX - rect.left - rect.width / 2) / 12);
    mouseY.set((e.clientY - rect.top - rect.height / 2) / 12);
    setSpotPos({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  }, [mouseX, mouseY]);

  return (
    <motion.div
      ref={ref}
      className="founder-hero"
      data-testid="team-member-0"
      style={{ rotateZ: rotate }}
    >
      {/* Large counter number */}
      <motion.span
        className="founder-counter"
        initial={{ opacity: 0, x: -60 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
      >
        01
      </motion.span>

      <div className="founder-layout">
        {/* Image */}
        <motion.div
          ref={imgRef}
          className="founder-img-wrap"
          onMouseMove={handleMove}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => { setHover(false); mouseX.set(0); mouseY.set(0); }}
          style={{ x: springX, y: springY }}
        >
          <motion.div
            className="founder-img-clip"
            initial={{ clipPath: 'inset(100% 0 0 0)' }}
            whileInView={{ clipPath: 'inset(0% 0 0 0)' }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 1.4, ease: [0.77, 0, 0.175, 1] }}
          >
            <motion.img
              src={founder.img}
              alt={founder.role}
              className="founder-img"
              style={{ y: imgY }}
              animate={{
                scale: hover ? 1.06 : 1,
                filter: hover ? 'grayscale(0.2) brightness(0.9)' : 'grayscale(0.8) brightness(0.7)',
              }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              loading="eager"
            />
            <div
              className="founder-spotlight"
              style={{
                opacity: hover ? 1 : 0,
                background: `radial-gradient(500px circle at ${spotPos.x}% ${spotPos.y}%, rgba(255,255,255,0.14) 0%, transparent 60%)`,
              }}
            />
          </motion.div>
          <motion.div
            className="founder-badge"
            animate={{ opacity: hover ? 0 : 1 }}
            transition={{ duration: 0.3 }}
            data-testid="team-avatar-0"
          >
            {founder.initials}
          </motion.div>
        </motion.div>

        {/* Text */}
        <motion.div className="founder-text" style={{ y: textY }}>
          <motion.p
            className="founder-label"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            The Founder
          </motion.p>
          <h3 className="founder-role-text">
            <SplitText text={founder.role} delay={0.5} />
          </h3>
          <motion.p
            className="founder-desc"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.7, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            {founder.desc}
          </motion.p>
          <motion.div
            className="founder-line"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
          />
        </motion.div>
      </div>
    </motion.div>
  );
}

/* ── Crew Card (Horizontal Scroll) ── */
function CrewCard({ member, index }) {
  const ref = useRef(null);
  const [hover, setHover] = useState(false);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springX = useSpring(mouseX, { stiffness: 150, damping: 20 });
  const springY = useSpring(mouseY, { stiffness: 150, damping: 20 });
  const [spotPos, setSpotPos] = useState({ x: 50, y: 50 });

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const imgScale = useTransform(scrollYProgress, [0, 0.5], [1.15, 1]);
  const cardY = useTransform(scrollYProgress, [0, 1], [40 + index * 20, -20]);

  const handleMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mouseX.set((e.clientX - rect.left - rect.width / 2) / 10);
    mouseY.set((e.clientY - rect.top - rect.height / 2) / 10);
    setSpotPos({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  }, [mouseX, mouseY]);

  const num = String(index + 2).padStart(2, '0');

  return (
    <motion.div
      ref={ref}
      className="crew-card"
      style={{ y: cardY }}
      data-testid={`team-member-${index + 1}`}
    >
      <motion.div
        className="crew-card-inner"
        onMouseMove={handleMove}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => { setHover(false); mouseX.set(0); mouseY.set(0); }}
        style={{ x: springX, y: springY }}
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{
          duration: 0.8,
          delay: 0.15 * index,
          ease: [0.16, 1, 0.3, 1],
        }}
      >
        {/* Counter */}
        <span className="crew-counter">{num}</span>

        {/* Image */}
        <motion.div
          className="crew-img-wrap"
          initial={{ clipPath: 'polygon(0 100%, 100% 100%, 100% 100%, 0 100%)' }}
          whileInView={{ clipPath: 'polygon(0 0%, 100% 0%, 100% 100%, 0 100%)' }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{
            duration: 1.2,
            delay: 0.2 + 0.15 * index,
            ease: [0.77, 0, 0.175, 1],
          }}
        >
          <motion.img
            src={member.img}
            alt={member.role}
            className="crew-img"
            style={{ scale: imgScale }}
            animate={{ filter: hover ? 'grayscale(0.2) brightness(0.9)' : 'grayscale(0.9) brightness(0.65)' }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            loading="lazy"
          />
          <div
            className="crew-spotlight"
            style={{
              opacity: hover ? 1 : 0,
              background: `radial-gradient(350px circle at ${spotPos.x}% ${spotPos.y}%, rgba(255,255,255,0.12) 0%, transparent 55%)`,
            }}
          />
          <motion.div
            className="crew-badge"
            animate={{ opacity: hover ? 0 : 1, scale: hover ? 0.8 : 1 }}
            transition={{ duration: 0.3 }}
            data-testid={`team-avatar-${index + 1}`}
          >
            {member.initials}
          </motion.div>
        </motion.div>

        {/* Text */}
        <div className="crew-text">
          <h3 className="crew-role">
            <SplitText text={member.role} delay={0.4 + 0.15 * index} />
          </h3>
          <motion.p
            className="crew-desc"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.7 + 0.1 * index }}
          >
            {member.desc}
          </motion.p>
        </div>

        {/* Hover glow border */}
        <motion.div
          className="crew-glow"
          animate={{ opacity: hover ? 1 : 0 }}
          transition={{ duration: 0.4 }}
        />
      </motion.div>
    </motion.div>
  );
}

/* ── Main Team Section ── */
export default function Team() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  const lineWidth = useTransform(scrollYProgress, [0.05, 0.25], ['0%', '100%']);
  const titleX = useTransform(scrollYProgress, [0.05, 0.3], [-40, 0]);
  const titleOp = useTransform(scrollYProgress, [0.05, 0.2], [0, 1]);

  return (
    <section
      ref={sectionRef}
      id="team"
      className="team-section-gsap"
      data-testid="team-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Animated line */}
        <motion.div className="team-divider" style={{ width: lineWidth }} />

        {/* Header */}
        <motion.p
          className="section-number mt-12"
          style={{ opacity: titleOp, x: titleX }}
          data-testid="team-number"
        >
          The Studio
        </motion.p>
        <motion.h2
          className="section-title mt-3 mb-6"
          style={{ opacity: titleOp }}
          data-testid="team-title"
        >
          The People Behind
          <br />
          the Work.
        </motion.h2>
        <motion.p
          className="team-subtitle"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        >
          Five people. One shared conviction that ideas with weight
          deserve architecture, not just acceleration.
        </motion.p>

        {/* Founder Hero */}
        <FounderCard />

        {/* Crew Grid */}
        <div className="crew-grid" data-testid="team-grid">
          {crew.map((member, i) => (
            <CrewCard key={member.initials} member={member} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
